// Pricing configuration for Inverio

export const CURRENCY = 'CHF';

export type BillingPeriod = 'monthly' | 'yearly';

export interface PricingTier {
  id: string;
  name: string;
  tagline: string;
  price: number;
  yearlyPrice?: number;
  period: BillingPeriod;
  features: string[];
  limits?: string[];
  limitations?: string[];
  highlighted?: boolean;
  ctaText: string;
  popular?: boolean;
}

// ===========================================
// FOUNDER/STARTUP PRICING TIERS
// ===========================================
export const startupPricing: PricingTier[] = [
  {
    id: 'startup-free',
    name: 'Free',
    tagline: 'Understand your readiness',
    price: 0,
    period: 'monthly',
    features: [
      'Pitch deck analysis (1/mo)',
      'Overall readiness score',
      'Confidence & risk rating',
      'Top 3 recommendations',
      'Basic valuation (Berkus)',
    ],
    limits: [
      '1 team seat',
    ],
    limitations: [
      'No full 15-dimension report',
      'No data room analysis',
      'No investor access',
    ],
    ctaText: 'Get Started',
  },
  {
    id: 'startup-starter',
    name: 'Starter',
    tagline: 'Prepare for investors',
    price: 29,
    yearlyPrice: 24,
    period: 'monthly',
    features: [
      'Everything in Free',
      'Full 15-dimension report',
      'Data room analysis',
      'Full valuation (4 methods)',
      'DD conversations (3/mo)',
      'Scoring refresh (1/mo)',
    ],
    limits: [
      '2 team seats',
      'Pitch deck analysis (3/mo)',
    ],
    limitations: [
      'No investor access',
      'No matching',
    ],
    ctaText: 'Start Preparing',
  },
  {
    id: 'startup-standard',
    name: 'Standard',
    tagline: 'Connect with investors',
    price: 49,
    yearlyPrice: 41,
    period: 'monthly',
    features: [
      'Everything in Starter',
      'View investor database',
      'Direct messaging (5/mo)',
      'Investor activity insights',
      'DD conversations (10/mo)',
      'Scoring refresh (3/mo)',
    ],
    limits: [
      '4 team seats',
      'Pitch deck analysis (10/mo)',
    ],
    highlighted: true,
    popular: true,
    ctaText: 'Start Raising',
  },
  {
    id: 'startup-premium',
    name: 'Premium',
    tagline: 'Accelerate fundraising',
    price: 99,
    yearlyPrice: 82,
    period: 'monthly',
    features: [
      'Everything in Standard',
      'Automatic investor matching',
      'Investor feedback loop',
      'Priority visibility in dealflow',
      'Unlimited messaging/scoring/DD',
    ],
    limits: [
      '6 team seats',
      'Unlimited everything',
    ],
    ctaText: 'Go Premium',
  },
];

// ===========================================
// INVESTOR PRICING TIERS
// ===========================================
export const investorPricing: PricingTier[] = [
  {
    id: 'investor-free',
    name: 'Free',
    tagline: 'Centralise your inbound',
    price: 0,
    period: 'monthly',
    features: [
      'Inbox (receive pitch decks)',
      'Basic scoring of uploads',
      'Basic filters',
      'Watchlist (10 startups)',
    ],
    limits: [
      '50 pitch decks (lifetime)',
    ],
    limitations: [
      'No dealflow browsing',
      'No DD reports',
      'No messaging',
    ],
    ctaText: 'Get Started',
  },
  {
    id: 'investor-starter',
    name: 'Starter',
    tagline: 'Casual angel (<5 deals/yr)',
    price: 59,
    yearlyPrice: 49,
    period: 'monthly',
    features: [
      'Everything in Free',
      'Summary & full DD reports',
      'DD conversations (5/mo)',
      'Platform dealflow',
      'Direct messaging (10/mo)',
    ],
    limits: [
      '100 scorings/mo',
      'Watchlist (50)',
    ],
    limitations: [
      'No pipeline management',
      'No portfolio tracking',
      'No thesis matching',
    ],
    ctaText: 'Start Investing',
  },
  {
    id: 'investor-standard',
    name: 'Standard',
    tagline: 'Active angel (5–15 deals/yr)',
    price: 99,
    yearlyPrice: 82,
    period: 'monthly',
    features: [
      'Everything in Starter',
      'Thesis fit analysis',
      'Feedback to founders',
      'Deal pipeline view',
      'Portfolio tracking',
      'Advanced filters',
    ],
    limits: [
      '300 scorings/mo',
      'DD conversations (20/mo)',
      'Messaging (50/mo)',
      'Watchlist (200)',
    ],
    highlighted: true,
    popular: true,
    ctaText: 'Get Standard',
  },
  {
    id: 'investor-premium',
    name: 'Premium',
    tagline: 'Super angel/Syndicate (15+ deals/yr)',
    price: 199,
    yearlyPrice: 166,
    period: 'monthly',
    features: [
      'Everything in Standard',
      'Priority deal access',
      'Auto-matching',
      'Syndicate tools',
    ],
    limits: [
      'Unlimited everything',
    ],
    ctaText: 'Go Premium',
  },
];

// ===========================================
// HELPER FUNCTIONS
// ===========================================
export const formatPrice = (price: number, period: BillingPeriod): string => {
  if (price === 0) return 'Free';
  if (period === 'yearly') return `${CURRENCY} ${price}/yr`;
  return `${CURRENCY} ${price}/mo`;
};

export const formatPriceShort = (price: number): string => {
  if (price === 0) return 'Free';
  return `${CURRENCY} ${price}`;
};

export const calculateYearlySavings = (monthlyPrice: number, yearlyPrice: number): number => {
  const yearlyCostAtMonthly = monthlyPrice * 12;
  const yearlyCostAtYearly = yearlyPrice * 12;
  return yearlyCostAtMonthly - yearlyCostAtYearly;
};

export const getYearlySavingsPercent = (monthlyPrice: number, yearlyPrice: number): number => {
  if (monthlyPrice === 0) return 0;
  return Math.round(((monthlyPrice - yearlyPrice) / monthlyPrice) * 100);
};
